/**
 * `/account` — the address, the name, and the browsers holding a session.
 *
 * The device list is the only screen in the portal with a security job. Its
 * whole purpose is to let somebody answer "is there anything here I do not
 * recognise" and do something about it in one click, so it lists live sessions
 * only: a page of expired and revoked rows is noise on the one screen people
 * open precisely because they are already worried.
 *
 * ── Why there is no "change my email" ───────────────────────────────────────
 *
 * Moving an address means re-pointing `licenses.email_hash` on every license
 * bound to the old one — a write in aloftwx-api's database, which this repo
 * cannot make and which the customer RPC contract deliberately does not expose.
 * Offering the half we can do would produce an account that signs in and owns
 * nothing, which is strictly worse than not offering it. The page says so and
 * points at support, where an operator does it from the console.
 */

import { PageHeader } from "@/components/PageHeader";
import { Empty, LeaderRow, Notice, Strip, StripBody, StripHead, Tag } from "@/components/deck";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { portalApi, useApiMutation, useApiQuery } from "@/lib/api";
import type { SessionRecord } from "@/lib/api";
import { isoDate, relative, stamp } from "@/lib/format";
import { useSession, useSignedIn } from "@/lib/session";
import { useToast } from "@/lib/toast";
import { LogOut, MonitorSmartphone } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router";

export function AccountRoute() {
  const session = useSignedIn();

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        code="ACC"
        label="Account"
        title="Your account"
        description="How we know you, and which browsers are currently signed in."
      />

      <div className="flex flex-col gap-6">
        <IdentityPanel email={session.email} memberSince={session.member_since} />
        <DevicesPanel />
        <SignOutPanel />
      </div>
    </div>
  );
}

function IdentityPanel({ email, memberSince }: { email: string; memberSince: string }) {
  const { pushToast } = useToast();
  const [name, setName] = useState("");
  const [saved, setSaved] = useState<string | null>(null);

  const save = useApiMutation((value: string) => portalApi.setDisplayName(value || null), {
    onSuccess: (result) => {
      setSaved(result.display_name);
      pushToast({ kind: "success", title: "Saved." });
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate(name.trim());
  }

  return (
    <section aria-labelledby="identity-head">
      <Strip bar={false}>
        <StripHead title="Identity" as="h2" id="identity-head" />
        <StripBody className="flex flex-col gap-3">
          <LeaderRow label="Email" value={email} />
          <LeaderRow label="Member since" value={isoDate(memberSince)} />
        </StripBody>

        <div className="border-t border-rule px-4 py-4">
          <form onSubmit={onSubmit}>
            <label htmlFor="display-name" className="t-field block">
              Name (optional)
            </label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Input
                id="display-name"
                name="display_name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={saved ?? "How we should say hello"}
                maxLength={60}
                className="max-w-xs flex-1"
              />
              <Button type="submit" variant="outline" disabled={save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
            <p className="mt-2 text-[0.8125rem] leading-snug text-ink-2">
              Only used to open an email politely. Leave it empty and we will not use one.
            </p>
          </form>
        </div>

        <div className="border-t border-rule px-4 py-4">
          <div className="t-field">Changing your email</div>
          <p className="mt-1.5 text-[0.9375rem] leading-relaxed text-ink-2">
            Your licenses are tied to this address, so moving it means moving them — which we do by
            hand rather than risk leaving you with an account that owns nothing.{" "}
            <Link to="/support" className="text-ink underline underline-offset-4">
              Ask us
            </Link>{" "}
            and it takes a minute.
          </p>
        </div>
      </Strip>
    </section>
  );
}

function DevicesPanel() {
  const { pushToast } = useToast();
  const devices = useApiQuery([], (signal) => portalApi.listDevices(signal), {
    suppressToast: true,
  });

  const revokeOne = useApiMutation((id: string) => portalApi.revokeDevice(id), {
    onSuccess: () => {
      pushToast({ kind: "success", title: "That browser has been signed out." });
      devices.refetch();
    },
  });

  const revokeOthers = useApiMutation(() => portalApi.revokeOtherDevices(), {
    onSuccess: (result) => {
      pushToast({
        kind: "success",
        title:
          result.revoked > 0
            ? `Signed out ${result.revoked} other ${result.revoked === 1 ? "browser" : "browsers"}.`
            : "There was nothing else signed in.",
      });
      devices.refetch();
    },
  });

  const list = devices.data?.devices ?? [];
  const others = list.filter((d) => !d.current).length;

  return (
    <section aria-labelledby="devices-head">
      <Strip bar={false}>
        <StripHead
          title="Signed-in browsers"
          as="h2"
          id="devices-head"
          note="Each one is a browser that can open your licenses without asking for a new link."
        >
          {others > 0 ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => revokeOthers.mutate(undefined)}
              disabled={revokeOthers.isPending}
            >
              {revokeOthers.isPending ? "Signing out…" : `Sign out the other ${others}`}
            </Button>
          ) : null}
        </StripHead>

        {devices.isLoading ? (
          <Empty>Loading…</Empty>
        ) : devices.error ? (
          <Notice
            tone="hold"
            label="Could not load"
            className="m-4"
            action={
              <Button variant="outline" size="sm" onClick={devices.refetch}>
                Try again
              </Button>
            }
          >
            {devices.error.message}
          </Notice>
        ) : list.length === 0 ? (
          <Empty>Nothing is signed in, which should not be possible from this page.</Empty>
        ) : (
          <div>
            {list.map((device) => (
              <DeviceRow
                key={device.id}
                device={device}
                busy={revokeOne.isPending}
                onRevoke={() => revokeOne.mutate(device.id)}
              />
            ))}
          </div>
        )}
      </Strip>
    </section>
  );
}

function DeviceRow({
  device,
  busy,
  onRevoke,
}: {
  device: SessionRecord;
  busy: boolean;
  onRevoke: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-rule px-4 py-4 last:border-b-0">
      <MonitorSmartphone aria-hidden="true" className="size-4 shrink-0 text-ink-3" />

      <div className="min-w-0 flex-1 basis-44">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[0.9375rem] text-ink">{device.agent}</span>
          {device.current ? (
            <Tag tone="cleared" dot={false}>
              THIS BROWSER
            </Tag>
          ) : null}
        </div>
        <div className="t-field mt-1">Signed in {isoDate(device.created_at)}</div>
      </div>

      <div className="min-w-0">
        <div className="t-field">Last used</div>
        <div className="t-data mt-1 text-[0.875rem] text-ink" title={stamp(device.last_seen_at)}>
          {relative(device.last_seen_at)}
        </div>
      </div>

      {/* No button on the current row: signing yourself out from a list of
          devices, on the page you are standing on, is a trap. The dedicated
          Sign out control below does that, and says what it is doing. */}
      {device.current ? (
        <span className="ml-auto shrink-0 text-[0.8125rem] text-ink-3">—</span>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={onRevoke}
          disabled={busy}
          className="ml-auto shrink-0"
        >
          Sign out
        </Button>
      )}
    </div>
  );
}

function SignOutPanel() {
  const navigate = useNavigate();
  const { setState } = useSession();
  const { pushToast } = useToast();

  const signOut = useApiMutation(() => portalApi.signOut(), {
    suppressToast: true,
    onSuccess: (state) => {
      setState(state);
      navigate("/signin", { replace: true });
    },
    onError: () =>
      pushToast({
        kind: "error",
        title: "We could not sign you out cleanly.",
        body: "Reload the page and try again.",
      }),
  });

  return (
    <section aria-labelledby="signout-head">
      <Strip bar={false}>
        <StripHead title="This browser" as="h2" id="signout-head" />
        <StripBody className="flex flex-wrap items-center justify-between gap-4">
          <p className="min-w-0 flex-1 basis-64 text-[0.9375rem] leading-relaxed text-ink-2">
            Signing out here does not affect your licenses or the app on your PC — those run on the
            key, not on this session.
          </p>
          <Button
            variant="outline"
            onClick={() => signOut.mutate(undefined)}
            disabled={signOut.isPending}
            className="shrink-0"
          >
            <LogOut aria-hidden="true" />
            {signOut.isPending ? "Signing out…" : "Sign out"}
          </Button>
        </StripBody>
      </Strip>
    </section>
  );
}
